// components/Common/NumberHours.tsx
import React from "react";
import { InputNumber, Tooltip, message, Tag } from "antd";
import CustomButton from "components/Common/ui/CustomButton";
import { useTranslation } from "react-i18next";
// import { updateClassLevelSubjectSetting } from "services/classLevelSubjectSetting.service";
import { ClassLevelSubjectSettingApiResponse } from "types/classLevelSubjectSettingTypes";
import { updateClassLevelSubjectSetting } from "services/classLevelSubjectSettingService";
import useClassLevelSubjectSetting from "feature-module/settings/hooks/useClassLevelSubjectSetting";

type Props = {
  settingId?: string;             // update-only: required to enable editing
  value?: number | null;          // current hours from parent (may be undefined/null initially)
  disabled?: boolean;
  editable?: boolean;             // <--- NEW: controls whether edit UI is shown (default true)
  min?: number;                   // default 0
  max?: number;                   // default 99
  fullWidth?: boolean;            // default true
  compact?: boolean;              // default false
  classLevelId?: string;          // for creating new settings
  subjectId?: string;             // for creating new settings
  onCreateSetting?: (payload: { classLevelId: string; subjectId: string; numberHours: number; }) => Promise<any>;
  onPatched?: (nextNumberHours: number, newSetting?: any) => void;
};

const NumberHours: React.FC<Props> = ({
  settingId,
  value = null,
  disabled = false,
  editable = true, // default is editable
  min = 0,
  max = 99,
  fullWidth = true,
  compact = false,
  classLevelId,
  subjectId,
  onCreateSetting,
  onPatched,
}) => {
  const { t } = useTranslation();

  const [mode, setMode] = React.useState<"view" | "edit">("view");
  const [current, setCurrent] = React.useState<number>(Number(value ?? 0));
  const [saving, setSaving] = React.useState(false);
  const { updateItemClassLevelSubjectSetting } = useClassLevelSubjectSetting();

  const ignoreNextPropSyncRef = React.useRef(false);

  React.useEffect(() => {
    console.log("value every useEffect", value);
    if (ignoreNextPropSyncRef.current) {
      ignoreNextPropSyncRef.current = false;
      return;
    }
    if (value !== null && value !== undefined) {
      const next = Number(value);
      if (!Number.isNaN(next) && next !== current) {
        setCurrent(next);
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = editable && (!!settingId || (!!classLevelId && !!subjectId && !!onCreateSetting)) && !disabled;
  const isDirty = Number(current ?? 0) !== Number(value ?? 0);

  const startEdit = () => {
    if (!canEdit) return;
    // setCurrent(Number(value ?? 0));
    setMode("edit");
  };

  const cancelEdit = () => {
    // setCurrent(Number(value ?? 0));
    setMode("view");
  };

  const doUpdate = async () => {
    if (!isDirty) return;
    
    try {
      setSaving(true);
      let res;

      if (settingId) {
        // Update existing setting
        res = await updateItemClassLevelSubjectSetting(settingId, { numberHours: Number(current ?? 0) });
      } else if (classLevelId && subjectId && onCreateSetting) {
        // Create new setting
        res = await onCreateSetting({
          classLevelId,
          subjectId,
          numberHours: Number(current ?? 0)
        });
      } else {
        return;
      }

      const next = Number(res?.data?.numberHours ?? current);
      console.log("res", res);

      ignoreNextPropSyncRef.current = true;
      setCurrent(next);
      setMode("view");
      
      // Pass the new setting data if it was created
      if (!settingId && res) {
        onPatched?.(next, res.data);
      } else {
        onPatched?.(next);
      }

      // message.success(t("saved_successfully") || "Saved");
    } catch (e: any) {
      message.error(e?.message || t("error_saving") || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" && isDirty && !saving) doUpdate();
    if (e.key === "Escape") cancelEdit();
  };

  return (
    <div className={`d-flex align-items-center ${compact ? "gap-1" : "gap-2"}`} style={{ width: "100%" }}>
      {mode === "view" || !editable ? (
        <>
          <div style={{ flex: fullWidth ? 1 : undefined }}>
            {current === null || current === undefined ? <Tag>—</Tag> : <Tag>{Number(current)} {t("hours")}</Tag>}
          </div>
          {editable && (
            <Tooltip
              title={
                canEdit ? (t("edit") || "Edit") : (settingId ? t("no_setting_to_update") || "No setting to update" : t("click_to_create_setting") || "Click to create setting")
              }
            >
              <CustomButton
                variant="light"
                size="sm"
                className="p-1"
                disabled={!canEdit}
                onClick={startEdit}
              >
                <i className="ti ti-edit" />
              </CustomButton>
            </Tooltip>
          )}
        </>
      ) : (
        <>
          <InputNumber
            min={min}
            max={max}
            value={current}
            onChange={(v) => setCurrent(Number(v ?? 0))}
            onKeyDown={onKeyDown}
            style={{ width: fullWidth ? "100%" : 110 }}
            disabled={saving}
          />
          <Tooltip title={t("save") || "Save"}>
            <CustomButton
              variant="primary"
              size="sm"
              className="p-1"
              disabled={!isDirty || saving}
              onClick={doUpdate}
            >
              <i className="ti ti-check" />
            </CustomButton>
          </Tooltip>
          <Tooltip title={t("cancel") || "Cancel"}>
            <CustomButton
              variant="secondary"
              size="sm"
              className="p-1"
              disabled={saving}
              onClick={cancelEdit}
            >
              <i className="ti ti-x" />
            </CustomButton>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default NumberHours;
